import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Controller, useFieldArray, useForm, UseFormSetValue } from 'react-hook-form';
import Select from 'react-select';

import { readPerson } from '@/modules/people/person.service';
import {
  PersonFormDto,
  VinculoDefensorAssessorFormDto,
} from '@/modules/people/presentation/person-form.utils';
import { validarVinculoDefensorAssessor } from '@/modules/people/presentation/forms/profiles/vinculo-assessor-defensor.utils';
import { DataAmigavel } from '@/shared/utils/datas';

type DefensorOption = { value: number; label: string };
type ModalForm = { vinculos: VinculoDefensorAssessorFormDto[] };

interface Props {
  show: boolean;
  handleHide: () => void;
  vinculos: VinculoDefensorAssessorFormDto[];
  setValue: UseFormSetValue<PersonFormDto>;
}

export default function GerenciarVinculosDefensoresModal({
  show,
  handleHide,
  vinculos,
  setValue,
}: Props) {
  const { control, register, reset, getValues, setError, clearErrors, formState: { errors } } =
    useForm<ModalForm>({ defaultValues: { vinculos: [] } });
  const { fields, append, remove } = useFieldArray({ control, name: 'vinculos', keyName: '_id' });
  const [defensores, setDefensores] = useState<DefensorOption[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    reset({ vinculos: vinculos.map((item) => ({ ...item })) });
    setErroGeral(null);
    setCarregando(true);
    readPerson({ skip: 0, take: 9000, comDefensor: true, isDefensor: true, orderBy: 'ASC' })
      .then(([pessoas]) => {
        setDefensores(
          pessoas
            .filter((pessoa) => pessoa.defensor)
            .map((pessoa) => ({ value: pessoa.defensor!.id, label: pessoa.nome })),
        );
      })
      .catch(() => setDefensores([]))
      .finally(() => setCarregando(false));
  }, [show, vinculos, reset]);

  const salvar = () => {
    clearErrors();
    setErroGeral(null);
    const itens = getValues('vinculos');

    for (let index = 0; index < itens.length; index++) {
      const erro = validarVinculoDefensorAssessor(itens[index], itens, index);
      if (!erro) continue;
      if (erro.includes('final')) {
        setError(`vinculos.${index}.data_final`, { message: erro });
      } else if (erro.includes('inicial')) {
        setError(`vinculos.${index}.data_inicial`, { message: erro });
      } else if (erro.includes('defensor')) {
        setError(`vinculos.${index}.defensor_id`, { message: erro });
      } else {
        setErroGeral(erro);
      }
      return;
    }

    const normalizados = itens.map((item) => {
      const opcao = defensores.find((defensor) => defensor.value === Number(item.defensor_id));
      return {
        ...item,
        defensor_id: Number(item.defensor_id),
        defensor_nome: opcao?.label ?? item.defensor_nome ?? null,
        data_final: item.data_final || null,
      };
    });
    setValue('colaborador.vinculosDefensores', normalizados, {
      shouldDirty: true,
      shouldTouch: true,
    });
    handleHide();
  };

  return (
    <Modal show={show} onHide={handleHide} size="lg" centered>
      <Modal.Header closeButton className="py-2">
        <Modal.Title className="fs-6">Vínculos com defensores</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {erroGeral && <div className="alert alert-danger py-2 small">{erroGeral}</div>}
        <div className="d-flex justify-content-end mb-2">
          <Button
            type="button"
            size="sm"
            variant="outline-primary"
            onClick={() => append({ data_inicial: '', data_final: null })}
          >
            Adicionar período
          </Button>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle mb-0">
            <thead>
              <tr>
                <th>Defensor</th>
                <th style={{ width: 150 }}>Início</th>
                <th style={{ width: 150 }}>Fim</th>
                <th style={{ width: 85 }} className="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={field._id}>
                  <td>
                    <Controller
                      name={`vinculos.${index}.defensor_id`}
                      control={control}
                      render={({ field: selectField }) => {
                        const atual = Number(selectField.value);
                        const opcoes = defensores.some((item) => item.value === atual)
                          ? defensores
                          : atual
                            ? [{
                                value: atual,
                                label: getValues(`vinculos.${index}.defensor_nome`) || `Defensor #${atual}`,
                              }, ...defensores]
                            : defensores;
                        return (
                          <Select<DefensorOption>
                            classNamePrefix="react-select"
                            options={opcoes}
                            isLoading={carregando}
                            placeholder="Selecione"
                            value={opcoes.find((item) => item.value === atual) ?? null}
                            onChange={(option) => selectField.onChange(option?.value)}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 1060 }) }}
                          />
                        );
                      }}
                    />
                    {errors.vinculos?.[index]?.defensor_id && (
                      <div className="invalid-feedback d-block">
                        {errors.vinculos[index]?.defensor_id?.message}
                      </div>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type="date"
                      size="sm"
                      {...register(`vinculos.${index}.data_inicial`)}
                      isInvalid={!!errors.vinculos?.[index]?.data_inicial}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.vinculos?.[index]?.data_inicial?.message}
                    </Form.Control.Feedback>
                  </td>
                  <td>
                    <Form.Control
                      type="date"
                      size="sm"
                      title={
                        getValues(`vinculos.${index}.data_final`)
                          ? DataAmigavel(getValues(`vinculos.${index}.data_final`)!)
                          : 'Em aberto'
                      }
                      {...register(`vinculos.${index}.data_final`)}
                      isInvalid={!!errors.vinculos?.[index]?.data_final}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.vinculos?.[index]?.data_final?.message}
                    </Form.Control.Feedback>
                  </td>
                  <td className="text-end">
                    <Button type="button" size="sm" variant="outline-danger" onClick={() => remove(index)}>
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))}
              {fields.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-3">
                    Nenhum vínculo cadastrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button type="button" size="sm" variant="secondary" onClick={handleHide}>Cancelar</Button>
        <Button type="button" size="sm" variant="primary" onClick={salvar}>Confirmar</Button>
      </Modal.Footer>
    </Modal>
  );
}
